import {
  App as AntApp,
  Button,
  Table,
  Tag,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Typography,
  Dropdown,
  Checkbox,
  Drawer,
  Divider,
  Radio,
  Tooltip,
  FormInstance,
} from "antd";
import { PresetColorType } from "antd/es/_util/colors";
import { ColumnsType } from "antd/es/table";
import { ApiService } from "api/api.service";
import PageTitle from "components/PageTitle";
import { RoleTypes, UserDto, UserSchemaDto } from "dtos/user.dto";
import DashboardLayout from "layout/DashboardLayout";
import { useEffect, useMemo, useState } from "react";
import { LuEye, LuEyeOff, LuListFilter } from "react-icons/lu";
import {
  TbEdit,
  TbSearch,
  TbPasswordUser,
  TbPlus,
  TbDots,
  TbUserCancel,
  TbUserCheck,
  TbX,
  TbCheck,
  TbExclamationCircleFilled,
  TbCircleCheckFilled,
  TbReload,
  TbLogin,
} from "react-icons/tb";
import { useSearchParams } from "react-router-dom";
import SelectPlanModal from "./components/SelectPlanModal";
import { ItemType } from "antd/es/menu/interface";
import TextNumeric from "components/TextNumeric";
import { OrganisationDto } from "dtos/organisation.dto";
import { useAppSelector } from "store";
import { toRoleDisplayNames } from "utils/functions";
import SessionsModal from "components/Users/SessionModal";
import { MdOutlineComputer } from "react-icons/md";
import SessionBadge from "components/Users/SessionBadge";

export const roleColorMap: Record<RoleTypes, PresetColorType> = {
  [RoleTypes.SUPERADMIN]: "gold",
  [RoleTypes.ADMIN]: "blue",
  [RoleTypes.LEAD_ADMIN]: "cyan-inverse",
  [RoleTypes.LEAD_MANAGER]: "cyan",
  [RoleTypes.CASE_ADMIN]: "pink-inverse",
  [RoleTypes.CASE_MANAGER]: "pink",
  [RoleTypes.CONTENT_ADMIN]: "purple-inverse",
  [RoleTypes.CONTENT_MANAGER]: "purple",
  [RoleTypes.AFFILIATE_ADMIN]: "orange-inverse",
  [RoleTypes.AFFILIATE_MANAGER]: "orange",
  [RoleTypes.AFFILIATE]: "yellow",
  [RoleTypes.USER]: "green",
  [RoleTypes.ADMIN_READ_ONLY]: "volcano",
  [RoleTypes.DMS_EXTERNAL]: "volcano-inverse",
  [RoleTypes.NEGOTIATOR]: "red",
  [RoleTypes.ISO]: "red-inverse",
};
function Users() {
  const { message, notification, modal } = AntApp.useApp();
  const user = useAppSelector((state) => state.user);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDto | null>(null);
  const [filters, setFilters] = useState<{
    page: number;
    limit: number;
    role: string[];
    search?: string;
    startDate?: string;
    endDate?: string;
    sort?: string;
    ban_status?: string;
  }>({
    page: 1,
    limit: 10,
    role: [],
    search: "",
  });

  const users = ApiService.user.useGetPaginated(filters);
  const affiliateAdminsManagers = ApiService.user.useGetPaginated({
    page: 1,
    limit: 100,
    role: [RoleTypes.ADMIN, RoleTypes.AFFILIATE_ADMIN, RoleTypes.AFFILIATE_MANAGER],
  });
  const addUser = ApiService.user.useAddUser();
  const updateUser = ApiService.user.useUpdateUser();
  const updatePasswordUser = ApiService.user.useUpdatePasswordUser();
  const deleteUserMutation = ApiService.user.useDeleteUser();
  const banUnbanUserMutation = ApiService.user.useBanorUnbanUser();
  const validateCode = ApiService.user.useValidateAffiliateCode();
  const resetAttempt = ApiService.user.useResetOTPAttemptsUser();
  const loginAsOther = ApiService.user.useLoginAsOther();

  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isUpdateUserModalOpen, setIsUpdateUserModalOpen] = useState(false);
  const [isChangePasswordUserModalOpen, setIsChangePasswordUserModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [addUserForm] = Form.useForm();
  const [updateUserForm] = Form.useForm();
  const [changePasswordForm] = Form.useForm();
  const [isAssignPlanModalOpen, setIsAssignPlanModalOpen] = useState(false);
  const [serachParams, setSearchParams] = useSearchParams();
  const [showTick, setShowTick] = useState<null | boolean>(null);
  const [recentPortalKey, setRecentPortalKey] = useState<string | null>(null);
  const regeneratePortalKey = ApiService.user.useRegeneratePortalAccessKey();
  const affiliatePlansList = ApiService.affiliatePlans.useGetAllPaginated({
    page: 1,
    limit: 1000,
  });
  const affiliatePlansData = affiliatePlansList.data?.data;

  const userIds = useMemo(() => users?.data?.result?.data?.map((u) => u._id) ?? [], [users?.data?.result?.data]);
  const sessionStatus = ApiService.session.useBulkStatus(userIds);
  const terminateAllMutation = ApiService.session.useTerminateAll();

  const columns: ColumnsType<UserSchemaDto> = [
    {
      key: "name",
      dataIndex: "name",
      title: "Name",
      width: 120,
      render: (_: unknown, record: UserSchemaDto) => (
        <div className="flex flex-col">
          <span>{record.name}</span>
          {user?.role === RoleTypes.SUPERADMIN ? (
            <SessionBadge
              userId={record._id}
              statusMap={sessionStatus.data}
              isLoading={sessionStatus.isLoading}
              onClick={() => {
                setSelectedUser(record);
                setSessionModalOpen(true);
              }}
            />
          ) : null}
        </div>
      ),
    },
    {
      key: "email",
      dataIndex: "email",
      title: "Email",
      width: 120,
    },
    {
      key: "phone",
      dataIndex: "phone",
      title: "Phone",
      width: 80,
      render: (_, record) => {
        return record?.phone?.number ? (
          <div className="flex gap-1 items-center">
            <TextNumeric>
              +{record?.phone?.code} {record?.phone?.number}
            </TextNumeric>
            {record?.phone?.verified ? (
              <Tooltip title="Phone number verified">
                <TbCircleCheckFilled className="text-green-600" />
              </Tooltip>
            ) : (
              <Tooltip title="Phone number not verified">
                <TbExclamationCircleFilled className="text-orange-500" />
              </Tooltip>
            )}
          </div>
        ) : (
          "-"
        );
      },
    },
    {
      key: "role",
      dataIndex: "role",
      title: "Role",
      width: 80,

      render: (text, record) => <Tag color={roleColorMap[record.role]}>{toRoleDisplayNames(record.role)}</Tag>,
    },
    {
      key: "affiliate_code",
      dataIndex: "affiliate_code",
      title: "Affiliate Code",
      width: 80,
    },
    {
      title: "Banned?",
      key: "is_banned",
      width: 80,
      render: (text, record) => <>{record.is_banned ? <Tag color="red">Yes</Tag> : <Tag color="green">No</Tag>}</>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => {
        let menuItems: ItemType[] = [
          ...(user.role === RoleTypes.SUPERADMIN
            ? [
                {
                  key: "edit",
                  icon: <TbEdit />,
                  label: "Edit",
                  onClick: () => {
                    openEditModal(record);
                  },
                },
              ]
            : []),
          // {
          //   key: "edit",
          //   icon: <TbEdit />,
          //   label: "Edit",
          //   onClick: () => {
          //     openEditModal(record);
          //   },
          // },
          {
            key: "change_password",
            icon: <TbPasswordUser />,
            label: "Change Password",
            disabled: user.role !== RoleTypes.SUPERADMIN && record._id !== user._id,
            onClick: () => {
              openPasswordModal(record);
            },
          },
          ...(user.role === RoleTypes.SUPERADMIN || user.role === RoleTypes.ADMIN
            ? [
                {
                  key: "terminate-sessions",
                  icon: <MdOutlineComputer />,
                  label: "View Active Sessions",

                  onClick: () => {
                    setSelectedUser(record);
                    setSessionModalOpen(true);
                  },
                },
              ]
            : []),
          ...(user.role === RoleTypes.SUPERADMIN
            ? [
                {
                  key: "ban",
                  icon: record.is_banned ? (
                    <TbUserCheck className="stroke-green-600" />
                  ) : (
                    <TbUserCancel className="stroke-yellow-600" />
                  ),
                  label: record.is_banned ? "Unban" : "Ban",
                  onClick: () => {
                    modal.confirm({
                      title: (
                        <>
                          Do you want to {record.is_banned ? "unban" : "ban"} <b>{record?.name}</b>?
                        </>
                      ),
                      okText: "Yes, I am sure",
                      onOk: () => handleBanUnbanUser(record._id, record.is_banned),
                      okButtonProps: {
                        loading: banUnbanUserMutation.isPending,
                      },
                      icon: <TbExclamationCircleFilled className="fill-yellow-500 size-12 mr-4" />,
                    });
                  },
                },
              ]
            : []),
          {
            key: "reset-attempt",
            icon: <TbReload />,
            danger: true,
            label: "Reset OTP Attempts",
            onClick: () => {
              modal.confirm({
                title: (
                  <>
                    Do you want to reset OTP attempts for <b>{record?.name}</b>?
                  </>
                ),
                okText: "Yes, I am sure",
                onOk: () => {
                  resetAttempt.mutate(
                    { id: record._id },
                    {
                      onSuccess: () => {
                        message.success("OTP attempts have been reset for this user.");
                      },
                    },
                  );
                },
                okButtonProps: {
                  danger: true,
                  color: "primary",
                  variant: "solid",
                  loading: resetAttempt.isPending,
                },
                icon: <TbExclamationCircleFilled className="fill-red-500 size-12 mr-4" />,
              });
            },
          },
          {
            key: "regenerate-portal-access-key",
            icon: <TbReload />,
            danger: true,
            label: "Regenerate Portal Access Key",
            onClick: () => {
              modal.confirm({
                title: (
                  <>
                    Do you want to regenerate portal access key for <b>{record?.name}</b>?
                  </>
                ),
                okText: "Yes, I am sure",
                onOk: () => {
                  regeneratePortalKey.mutate(record._id, {
                    onSuccess: (data) => {
                      const { portal_access_key } = data;
                      message.success("Portal access key has been reset for this user.");
                      setRecentPortalKey(portal_access_key);
                    },
                  });
                },
                okButtonProps: {
                  danger: true,
                  color: "primary",
                  variant: "solid",
                  loading: resetAttempt.isPending,
                },
                icon: <TbExclamationCircleFilled className="fill-red-500 size-12 mr-4" />,
              });
            },
          },
          {
            key: "login-user",
            icon: <TbLogin />,
            label: "Login as this user",
            disabled: record._id === user._id,
            onClick: () => {
              if (user.role !== RoleTypes.ADMIN && user.role !== RoleTypes.SUPERADMIN) {
                message.error("This action can only be performed by admins or superadmins.");
                return;
              }
              Modal.confirm({
                title: "Are you sure you want to login as this user?",
                okText: "Yes",
                cancelText: "No",
                centered: true,
                onOk: () => {
                  loginAsOther.mutate(
                    { email: record.email },
                    {
                      onSuccess: () => {
                        message.success(`Succesfully logged in as ${record.name}!`);
                      },
                    },
                  );
                },
              });
            },
          },
        ];
        if (
          [RoleTypes.AFFILIATE_ADMIN, RoleTypes.AFFILIATE_MANAGER, RoleTypes.AFFILIATE, RoleTypes.ISO].includes(
            record.role,
          )
        ) {
          const affiliatePlan: ItemType = {
            key: "add_plan",
            icon: <TbPlus />,
            label: "Affiliate Plan",
            onClick: () => {
              openAssignPlanModal(record._id);
            },
          };
          menuItems = [affiliatePlan, ...menuItems];
        }

        return (
          <>
            {/* <Button icon={<TbEdit />} type="text" onClick={() => openEditModal(record)} />
            {record.role === RoleTypes.AFFILIATE && (
              <Button icon={<TbPlus />} type="text" onClick={() => openAssignPlanModal(record._id)} />
            )}
            <Popconfirm
              title="Do you want to remove the user from the platform?"
              onConfirm={() => deleteUser(record._id)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<TbTrash style={{ color: "red", fontSize: "20px" }} />} type="text"></Button>
            </Popconfirm>
            <Popconfirm
              title={`Do you want to ${record.is_banned ? "Unban" : "Ban"} this user?`}
              onConfirm={() => handleBanUnbanUser(record._id, record.is_banned)}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<TbBan style={{ color: "red", fontSize: "20px" }} />} type="text"></Button>
            </Popconfirm>
            <Button icon={<TbPasswordUser />} type="text" onClick={() => openPasswordModal(record)} /> */}
            <Dropdown
              menu={{
                items: menuItems,
              }}
            >
              <Button icon={<TbDots />} />
            </Dropdown>
          </>
        );
      },
    },
  ];
  const openAssignPlanModal = (id?: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (id) {
        params.set("id", id);
      } else {
        params.delete("id");
      }
      return params;
    });
    setIsAssignPlanModalOpen(true);
  };

  const deleteUser = (userid: string) => {
    deleteUserMutation.mutate(
      { userid },
      {
        onSuccess: () => {
          message.success("User removed successfully");
          users.refetch();
        },
        onError: (error) => {
          message.error("Failed to remove user");
          console.error("Delete User Error:", error);
        },
      },
    );
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setFilters((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleBanUnbanUser = (userid: string, status: boolean) => {
    banUnbanUserMutation.mutate(
      { userid, is_banned: !status },
      {
        onSuccess: () => {
          message.success(`User has been ${status ? "unbanned" : "banned"} successfully`);
          users.refetch();
        },
        onError: (error) => {
          message.error("Failed to update user status");
          console.error("Ban/Unban User Error:", error);
        },
      },
    );
  };

  const openEditModal = (record: UserSchemaDto) => {
    setEditingUserId(record._id);
    setIsUpdateUserModalOpen(true);
    updateUserForm.setFieldsValue({
      name: record.name,
      email: record.email,
      role: record.role,
      affiliate_code: record.affiliate_code,
      assigned_to: record.assigned_to,
      phone: record.phone,
      organisation: record.organisation,
      sub_organisations: record.sub_organisations,
      affiliate_plan: record.affiliate_model?.plan?._id,
    });
  };

  const openPasswordModal = (record: UserSchemaDto) => {
    setEditingUserId(record._id);
    setIsChangePasswordUserModalOpen(true);
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters, page: 1 }));
  };

  useEffect(() => {
    if (!recentPortalKey) return;

    console.log(recentPortalKey);

    notification.info({
      message: "Portal Access Key for Web Integrations",
      description: (
        <div className="mt-2 space-y-4 text-base">
          <div>
            Your portal access key is:{" "}
            <b className="font-mono text-brand-500">
              {recentPortalKey.slice(0, 6) + "*".repeat(recentPortalKey.length - 6)}
            </b>
            . Please <b>copy</b> and store this key safely. This key will be <b>lost</b> once this screen is closed. If
            lost, you will need to <b>regenerate</b> a new one.
          </div>

          <div>
            <Button
              onClick={() => {
                if (!recentPortalKey) return;

                navigator.clipboard
                  .writeText(recentPortalKey)
                  .then(() => {
                    message.success("Copied successfully!");
                  })
                  .catch((err) => {
                    console.error("Clipboard copy failed", err);
                    message.error("Failed to copy. Try again.");
                  });
              }}
              type="primary"
              ghost
            >
              Click to copy
            </Button>
          </div>
        </div>
      ),
      placement: "bottomRight",
      duration: 0,
    });

    setRecentPortalKey(null);
  }, [recentPortalKey]);

  const organisations = ApiService.organisation.useGetAllOrganisations();
  const [filteredSubOrgs, setFilteredSubOrgs] = useState(
    organisations.data?.filter((org) => {
      const editingUser = users.data?.result.data.find((u) => u._id === editingUserId);
      return editingUser?.organisation?.toString() !== org._id.toString();
    }) || [],
  );

  useEffect(() => {
    if (!isUpdateUserModalOpen) return;

    const editingUser = users.data?.result.data.find((u) => u._id === editingUserId);
    if (!editingUser) return;

    const mainOrgId = editingUser.organisation;

    const filtered = organisations.data?.filter((org) => org._id.toString() !== mainOrgId?.toString()) || [];
    setFilteredSubOrgs(filtered);

    updateUserForm.setFieldsValue({
      sub_organisations: editingUser.sub_organisations || [],
      organisation: mainOrgId,
    });
  }, [isUpdateUserModalOpen, editingUserId, organisations.data, users.data, updateUserForm]);

  const PermissionSelector = ({
    organisations,
    form,
  }: {
    organisations: OrganisationDto[];
    form: FormInstance<any>;
  }) => {
    const initialValue: { organisation: string; permission: string }[] = form.getFieldValue("sub_organisations") || [];

    const [selectedOrgs, setSelectedOrgs] = useState(initialValue);

    useEffect(() => {
      form.setFieldValue("sub_organisations", selectedOrgs);
    }, [selectedOrgs, form]);

    const handleOrgSelect = (ids: string[]) => {
      const updated = ids.map((id) => {
        const existing = selectedOrgs.find((o) => o.organisation === id);
        return existing || { organisation: id, permission: "R" };
      });
      setSelectedOrgs(updated);
    };

    return (
      <Form.Item name={"sub_organisations"} label="Sub Organisations">
        <div>
          <Select
            mode="multiple"
            placeholder="Select organisations"
            value={selectedOrgs.map((o) => o.organisation)}
            onChange={handleOrgSelect}
            options={organisations?.map((el) => ({
              label: el.name,
              value: el._id.toString(),
            }))}
          />
          <Space direction="vertical" style={{ marginTop: 8 }}>
            {selectedOrgs.map((org) => {
              const orgName = organisations?.find((o) => o._id.toString() === org.organisation)?.name;
              return (
                <div className="flex gap-2 items-center" key={org.organisation}>
                  <strong>{orgName}</strong>
                  <Checkbox.Group
                    value={org.permission.split("")}
                    onChange={(checked) => {
                      const perms = checked.join("");
                      const updated = selectedOrgs.map((o) =>
                        o.organisation === org.organisation ? { ...o, permission: perms } : o,
                      );
                      setSelectedOrgs(updated);
                    }}
                  >
                    <Checkbox disabled value="R">
                      Read
                    </Checkbox>
                    <Checkbox value="E">Edit</Checkbox>
                    <Checkbox value="D">Delete</Checkbox>
                  </Checkbox.Group>
                </div>
              );
            })}
          </Space>
        </div>
      </Form.Item>
    );
  };

  return (
    <DashboardLayout
      headerRight={
        user.role === RoleTypes.SUPERADMIN ? (
          <Button
            type="primary"
            onClick={() => {
              setIsAddUserModalOpen(true);
            }}
          >
            Add User
          </Button>
        ) : null
      }
    >
      {isAssignPlanModalOpen && <SelectPlanModal isModalOpen setIsModalOpen={setIsAssignPlanModalOpen} />}

      <PageTitle title="Users" pageTitle={[{ title: "Users" }]} />
      <div className="flex justify-between gap-3 p-3">
        <div className="inline-flex gap-3 items-center">
          <Button
            icon={<LuListFilter />}
            onClick={() => {
              setIsFilterOpen(true);
            }}
          >
            Filter{" "}
          </Button>
          {/* <Space.Compact>
            <Dropdown
              trigger={["click"]}
              menu={{
                selectable: true,
                items: [
                  {
                    key: "createdAt",
                    label: "Created at",
                  },
                ],
              }}
            >
              <Button className="border-gray-300" icon={<TbArrowsUpDown />}>
                Sort
              </Button>
            </Dropdown>
          </Space.Compact> */}
        </div>
        <div className="inline-flex gap-3 items-center">
          <Input
            prefix={<TbSearch />}
            placeholder="Search applications..."
            allowClear
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
      </div>
      <Table
        size="small"
        columns={columns}
        dataSource={users?.data?.result?.data}
        loading={users.isLoading}
        pagination={{
          pageSize: filters.limit,
          current: filters.page,
          total: users?.data?.result?.totalObjects,
          onChange: (page: number, pageSize: number) => {
            setFilters({ ...filters, page, limit: pageSize });
          },
        }}
        rowKey={"_id"}
      />
      <Modal
        title="Add New User"
        open={isAddUserModalOpen}
        onCancel={() => {
          setIsAddUserModalOpen(false);
        }}
        onOk={() => {
          addUserForm.submit();
        }}
        okText="Submit"
        okButtonProps={{ loading: addUser.isPending }}
      >
        <Form
          layout="vertical"
          form={addUserForm}
          requiredMark="optional"
          onFinish={(values) => {
            addUser.mutate(values, {
              onSuccess: (data) => {
                const { portal_access_key: sequence } = data;

                console.log("here", sequence);
                setRecentPortalKey(sequence);

                addUserForm.resetFields();
                setIsAddUserModalOpen(false);
                users.refetch();
              },
            });
          }}
        >
          <Form.Item name={"name"} label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name={"email"} label="Email" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name={"password"} label="Password" rules={[{ required: true }]}>
            <Input.Password iconRender={(visible) => (visible ? <LuEye /> : <LuEyeOff />)} />
          </Form.Item>
          <div className="flex gap-2">
            <Form.Item
              name={"code"}
              label="Code"
              rules={[{ required: false, message: "Enter code" }]}
              style={{ width: 100 }}
              initialValue={"+1"}
            >
              <Select
                options={[
                  { value: "+1", label: "+1" },
                  { value: "+91", label: "+91" },
                ]}
              />
            </Form.Item>
            <Form.Item
              name={"phone"}
              label="Phone Number"
              rules={[{ required: false, pattern: /^[0-9]+$/, message: "Enter a valid phone number" }]}
              className="flex-1"
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item name={"role"} label="Role" rules={[{ required: true }]}>
            <Select options={Object.values(RoleTypes).map((el) => ({ value: el, label: toRoleDisplayNames(el) }))} />
          </Form.Item>
          <Form.Item shouldUpdate={(prev, curr) => prev.role !== curr.role} noStyle>
            {({ getFieldValue }) =>
              [RoleTypes.ISO, RoleTypes.AFFILIATE, RoleTypes.AFFILIATE_MANAGER, RoleTypes.AFFILIATE_ADMIN].includes(
                getFieldValue("role"),
              ) ? (
                <Form.Item
                  name="affiliate_plan"
                  label="Affiliate Plan"
                  rules={[{ required: true, message: "Please select an affiliate plan" }]}
                >
                  <Select
                    placeholder="Select Affiliate Plan"
                    style={{ width: "100%" }}
                    allowClear
                    options={affiliatePlansData?.map((plan) => ({
                      label: plan.name,
                      value: plan._id,
                    }))}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="organisation"
            label="Main Organisation"
            rules={[{ required: true, message: "Please select the main organisation." }]}
          >
            <Select
              options={organisations.data?.map((el) => ({ value: el._id, label: el.name }))}
              onChange={(mainOrgId) => {
                const currentSubs = addUserForm.getFieldValue("sub_organisations") || [];
                const filteredSubs = currentSubs.filter(
                  (sub: any) => sub.organisation.toString() !== mainOrgId.toString(),
                );
                updateUserForm.setFieldValue("sub_organisations", filteredSubs);
                setFilteredSubOrgs(
                  organisations.data?.filter((org) => org._id.toString() !== mainOrgId.toString()) || [],
                );
              }}
            />
          </Form.Item>
          <PermissionSelector organisations={filteredSubOrgs} form={addUserForm} />
          <Form.Item name={"assigned_to"} label="Manager">
            <Select
              allowClear
              showSearch
              placeholder="Search affiliate managers & admins..."
              options={affiliateAdminsManagers.data?.result.data?.map((el) => ({ value: el._id, label: el.name }))}
              filterOption={(searchTerm, option) => {
                return !!option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Update User"
        open={isUpdateUserModalOpen}
        onCancel={() => {
          setShowTick(null);
          setIsUpdateUserModalOpen(false);
        }}
        onOk={() => {
          setShowTick(null);
          updateUserForm.setFieldsValue("");
          updateUserForm.submit();
        }}
        okText="Submit"
        okButtonProps={{ loading: updateUser.isPending }}
      >
        <Form
          layout="vertical"
          form={updateUserForm}
          requiredMark="optional"
          onFinish={(values) => {
            updateUser.mutate(
              { userid: editingUserId, ...values },
              {
                onSuccess: () => {
                  console.log(values);
                  message.success("User updated");
                  updateUserForm.resetFields();
                  setIsUpdateUserModalOpen(false);
                  users.refetch();
                },
                onError: (error) => {
                  message.error(error.response?.data.message);
                },
              },
            );
          }}
        >
          <Form.Item name={"name"} label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name={"email"} label="Email" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <div className="flex gap-2">
            <Form.Item
              name={["phone", "code"]}
              label="Code"
              rules={[{ required: false, message: "Enter code" }]}
              style={{ width: 100 }}
              initialValue={"+1"}
            >
              <Select
                options={[
                  { value: "+1", label: "+1" },
                  { value: "+91", label: "+91" },
                ]}
              />
            </Form.Item>
            <Form.Item
              name={["phone", "number"]}
              label="Phone Number"
              rules={[{ required: false, pattern: /^[0-9]+$/, message: "Enter a valid phone number" }]}
              className="flex-1"
            >
              <Input />
            </Form.Item>
          </div>
          <Form.Item name={"role"} label="Role" rules={[{ required: true }]}>
            <Select options={Object.values(RoleTypes).map((el) => ({ value: el, label: toRoleDisplayNames(el) }))} />
          </Form.Item>
          <Form.Item shouldUpdate={(prev, curr) => prev.role !== curr.role} noStyle>
            {({ getFieldValue }) =>
              [RoleTypes.ISO, RoleTypes.AFFILIATE, RoleTypes.AFFILIATE_MANAGER, RoleTypes.AFFILIATE_ADMIN].includes(
                getFieldValue("role"),
              ) ? (
                <Form.Item
                  name="affiliate_plan"
                  label="Affiliate Plan"
                  rules={[{ required: true, message: "Please select an affiliate plan" }]}
                >
                  <Select
                    placeholder="Select Affiliate Plan"
                    style={{ width: "100%" }}
                    allowClear
                    options={affiliatePlansData?.map((plan) => ({
                      label: plan.name,
                      value: plan._id,
                    }))}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item
            name="organisation"
            label="Main Organisation"
            rules={[{ required: true, message: "Please select the main organisation." }]}
          >
            <Select
              options={organisations.data?.map((el) => ({ value: el._id, label: el.name }))}
              onChange={(mainOrgId) => {
                const currentSubs = addUserForm.getFieldValue("sub_organisations") || [];
                const filteredSubs = currentSubs.filter(
                  (sub: any) => sub.organisation.toString() !== mainOrgId.toString(),
                );
                updateUserForm.setFieldValue("sub_organisations", filteredSubs);
                setFilteredSubOrgs(
                  organisations.data?.filter((org) => org._id.toString() !== mainOrgId.toString()) || [],
                );
              }}
            />
          </Form.Item>
          <PermissionSelector organisations={filteredSubOrgs} form={updateUserForm} />

          <Form.Item name={"assigned_to"} label="Manager">
            <Select
              allowClear
              showSearch
              placeholder="Search affiliate managers & admins..."
              options={affiliateAdminsManagers.data?.result.data?.map((el) => ({ value: el._id, label: el.name }))}
              filterOption={(searchTerm, option) => {
                return !!option?.label?.toLowerCase()?.includes(searchTerm?.toLowerCase());
              }}
            />
          </Form.Item>
          <Form.Item
            name={"affiliate_code"}
            label={
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span>Affiliate Code</span>
                {showTick !== null && (showTick ? <TbCheck color="green" /> : <TbX color="red" />)}
              </div>
            }
            rules={[
              { required: true, max: 6 },
              {
                validator: (_, value) => {
                  if (value && /[Oo0iIlL]/.test(value)) {
                    return Promise.reject("Affiliate Code cannot contain O, I, L and zero (0).");
                  }
                  return Promise.resolve();
                },
              },
            ]}
            normalize={(value) => value.toUpperCase()}
          >
            <Input.Search
              enterButton="Validate Code"
              onSearch={(value) => {
                if (!value) {
                  message.error("Please enter an affiliate code.");
                  return;
                }

                validateCode.mutate(
                  { affiliate_code: value },
                  {
                    onSuccess: (data) => {
                      console.log(data);
                      setShowTick(!data);
                      if (data !== true) message.success("Code can be used.");
                      else message.error("Code cannot be used.");
                    },
                    onError: (error) => {
                      message.error(error.response?.data?.message || "Validation failed.");
                    },
                  },
                );
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Change User Password"
        open={isChangePasswordUserModalOpen}
        onCancel={() => {
          setIsChangePasswordUserModalOpen(false);
        }}
        onOk={() => {
          changePasswordForm.submit();
        }}
        okText="Submit"
        okButtonProps={{ loading: updatePasswordUser.isPending }}
      >
        <Form
          layout="vertical"
          form={changePasswordForm}
          requiredMark="optional"
          onFinish={(values) => {
            updatePasswordUser.mutate(
              { id: editingUserId, ...values },
              {
                onSuccess: () => {
                  // console.log(values);
                  message.success("User password updated");
                  changePasswordForm.resetFields();
                  setIsChangePasswordUserModalOpen(false);
                  // users.refetch();
                },
              },
            );
          }}
        >
          <Form.Item name={"newpassword"} label="Password" rules={[{ required: true }]}>
            <Input.Password iconRender={(visible) => (visible ? <LuEye /> : <LuEyeOff />)} />
          </Form.Item>
        </Form>
      </Modal>
      <FilterDrawer open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onFilterApply={handleFilterChange} />
      <SessionsModal
        open={sessionModalOpen}
        onClose={() => setSessionModalOpen(false)}
        userId={selectedUser?._id ?? null}
        userName={selectedUser?.name}
      />
    </DashboardLayout>
  );
}

type FilterType = {
  role: string[];
  ban_status: string;
  startDate: string;
  endDate: string;
};
function FilterDrawer({
  open,
  onClose,
  onFilterApply,
}: {
  open: boolean;
  onClose: () => void;
  onFilterApply: (filters: Partial<FilterType>) => void;
}) {
  const [localFilters, setLocalFilters] = useState({
    role: [] as string[],
    ban_status: "",
    startDate: "",
    endDate: "",
  });

  const handleApply = () => {
    onFilterApply(localFilters);
    onClose();
  };
  return (
    <Drawer
      open={open}
      onClose={onClose}
      footer={
        <div className="flex gap-2">
          <Button
            className="flex-1"
            onClick={() => {
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            variant="solid"
            color="primary"
            // onClick={() => {
            //   onClose();
            // }}
            onClick={handleApply}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 p-4">
        <Typography.Text type="secondary">Role</Typography.Text>
        <div className="flex flex-col gap-2 ml-4">
          {Object.entries(RoleTypes).map(([key, value]) => (
            <button
              className="flex items-center gap-2 bg-transparent border-none outline-none cursor-pointer p-0 m-0"
              key={key}
              onClick={() => {
                setLocalFilters((prev) => {
                  const currentRoles = prev.role || [];
                  return {
                    ...prev,
                    role: currentRoles.includes(value)
                      ? currentRoles.filter((r) => r !== value)
                      : [...currentRoles, value],
                  };
                });
              }}
            >
              <Checkbox checked={localFilters.role?.includes(value)} />
              <Tag color={roleColorMap[value]}>{toRoleDisplayNames(value)}</Tag>
            </button>
          ))}
        </div>

        <Divider className="!my-2" />

        <Typography.Text type="secondary">Ban Status</Typography.Text>
        <Radio.Group
          onChange={(e) => setLocalFilters((prev) => ({ ...prev, ban_status: e.target.value }))}
          options={[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ]}
        />

        {/* <Divider className="!my-2" />
        <Typography.Text type="secondary">Created At</Typography.Text>
        <Space direction="vertical">
          <DatePicker
            placeholder="Start Date"
            onChange={(_, dateString) => setLocalFilters((prev) => ({ ...prev, startDate: dateString as string }))}
          />
          <DatePicker
            placeholder="End Date"
            onChange={(_, dateString) => setLocalFilters((prev) => ({ ...prev, endDate: dateString as string }))}
          />
        </Space> */}
      </div>
    </Drawer>
  );
}

export default Users;
